import { EngineInstrumentation, SceneInstrumentation, Vector3 } from '@babylonjs/core';
import { createPhysicsWorld } from '@roadworld/physics';
import { DEFAULT_LOCATION, type GeocodeResult } from '@roadworld/shared';
import { AudioEngine } from './audio/AudioEngine.js';
import { EngineSound } from './audio/EngineSound.js';
import { ImpactSound } from './audio/ImpactSound.js';
import { PingSound } from './audio/PingSound.js';
import { TeleportWhoosh } from './audio/TeleportWhoosh.js';
import { TireSound } from './audio/TireSound.js';
import { TreeImpactSound } from './audio/TreeImpactSound.js';
import { WindSound } from './audio/WindSound.js';
import { createNoiseBuffer } from './audio/noise.js';
import { CameraController } from './camera/CameraController.js';
import { TeleportCinematic } from './camera/TeleportCinematic.js';
import { ClientOrderManager } from './delivery/OrderManager.js';
import { applyDiagnosticFromSettings } from './engine/Diagnostic.js';
import { createEngine } from './engine/Engine.js';
import { applyPaletteFromSettings, PALETTE } from './engine/palettes.js';
import { createPostFx, type PostFxHandle } from './engine/PostFx.js';
import { createScene } from './engine/Scene.js';
import { applyLinkedCameraDistance, flushSettings, loadSettings, saveSettings } from './engine/Settings.js';
import { createSky, type SkyHandle } from './engine/Sky.js';
import { createTimeOfDayController } from './engine/TimeOfDay.js';
import { Input } from './input/Input.js';
import { geocode } from './net/api.js';
import { resolvePlayerName } from './net/identity.js';
import { MultiplayerClient } from './net/MultiplayerClient.js';
import { TrafficManager } from './traffic/TrafficManager.js';
import { AreaToast } from './ui/AreaToast.js';
import { Hud } from './ui/Hud.js';
import { Minimap, ZOOM_LEVELS_M } from './ui/Minimap.js';
import { loadZoomIndex, saveZoomIndex, type AreaTier } from './ui/minimapPrefs.js';
import { SettingsModal } from './ui/SettingsModal.js';
import { createDebugSection } from './ui/settings/debugSection.js';
import { createMapSection } from './ui/settings/mapSection.js';
import { createSoundsSection, type SoundsBindings } from './ui/settings/soundsSection.js';
import { createVisualSection } from './ui/settings/visualSection.js';
import { saveAudioPrefs } from './ui/audioPrefs.js';
import { PlayerListPanel, type PlayerListEntry } from './ui/PlayerListPanel.js';
import { LoadingOverlay } from './ui/LoadingOverlay.js';
import { Toast } from './ui/Toast.js';
import * as vehiclePref from './ui/vehiclePref.js';
// SettingsPanel removed — its contents migrated into Visual + Debug sections of SettingsModal.
import { Car } from './vehicle/Car.js';
import type { IVehicle } from './vehicle/IVehicle.js';
import { Motorcycle } from './vehicle/Motorcycle.js';
import { AreaIndex } from './world/AreaIndex.js';
import { ChunkManager, DEFAULT_CHUNK_OPTIONS } from './world/ChunkManager.js';
import { GrassField } from './world/GrassField.js';
import { worldToLatLng, type SessionFrame } from './world/coords.js';
import { HeadlightSystem } from './world/Headlights.js';
import { PlaceIndex } from './world/PlaceIndex.js';
import { TreeImpactSystem } from './world/treeImpacts.js';

const CHUNK_SIZE = 1500;

async function main(): Promise<void> {
  const canvas = document.getElementById('renderCanvas') as HTMLCanvasElement | null;
  if (!canvas) throw new Error('Missing #renderCanvas');

  const { engine, backend } = await createEngine(canvas);
  console.info(`[engine] backend = ${backend}`);

  const settings = loadSettings();
  applyLinkedCameraDistance(settings);
  applyDiagnosticFromSettings(settings);
  // Palette must be applied BEFORE createScene reads PALETTE.horizon etc.
  applyPaletteFromSettings(settings);

  const { scene, shadowGenerator, sun, ambient } = createScene(engine);
  // Apply the user's fog-distance multiplier on top of the palette defaults.
  scene.fogStart = PALETTE.fogStart * settings.fogDistanceMul;
  scene.fogEnd = PALETTE.fogEnd * settings.fogDistanceMul;
  if (!settings.diagShadowsEnabled) scene.shadowsEnabled = false;
  // Motion blur UI is currently hidden; force it off so any previously-persisted
  // `motionBlurEnabled = true` doesn't re-activate the post process.
  settings.motionBlurEnabled = false;

  // Perf instrumentation — read by the HUD perf panel each frame. SceneInstrumentation
  // tracks draw calls; EngineInstrumentation pulls GPU frame time (timer queries; may be
  // null on WebGL without EXT_disjoint_timer_query).
  const sceneInstrumentation = new SceneInstrumentation(scene);
  sceneInstrumentation.captureActiveMeshesEvaluationTime = false;
  sceneInstrumentation.captureRenderTargetsRenderTime = false;
  sceneInstrumentation.captureFrameTime = false;
  // Draw calls counter is enabled by default; nothing to flip.
  const engineInstrumentation = new EngineInstrumentation(engine);
  engineInstrumentation.captureGPUFrameTime = true;

  // Camera controller wraps both modes (chase + top-down). The chase camera is what Sky
  // and PostFx bind to — they need a stable FreeCamera that always lives even while the
  // user is in top-down view.
  const cameraCtl = new CameraController(scene, settings);
  const tod = createTimeOfDayController(scene, sun, ambient);
  const headlights = new HeadlightSystem(scene);
  headlights.setCameraMode(cameraCtl.getMode());

  // Teleport cinematic: GTA-style fly-up + clouds + descend transition. Take over the
  // active camera and cover the screen with clouds RIGHT NOW so the page opens with the
  // cloud cover already in place — the inbound animation plays once loadAtInner has the
  // spawn chunk ready.
  const cinematic = new TeleportCinematic(scene);
  cinematic.setCovered({ cameraCtl, variant: 'short' });
  // Guard against starting a second cinematic while one is mid-flight.
  let cinematicBusy = false;

  // Loading lifecycle. While a section streams in we keep the screen covered with the
  // animated clouds, show a spinner + Cancel (LoadingOverlay), and lift the location bar
  // + players list above the cover so the user can redirect. `activeLoad` is the single
  // in-flight load; starting a new one supersedes it, and the Cancel button runs the
  // current load's `cancel` recovery (return to previous, or stay covered on first load).
  const loadingOverlay = new LoadingOverlay();
  let activeLoad: { ctrl: AbortController; cancel: () => void } | null = null;
  // The last location that finished loading — what a cancelled in-flight load returns to.
  let previousLocation: GeocodeResult | null = null;

  function beginLoad(cancel: () => void): AbortController {
    const ctrl = new AbortController();
    activeLoad = { ctrl, cancel };
    loadingOverlay.show();
    return ctrl;
  }
  function endLoad(ctrl: AbortController): void {
    if (activeLoad?.ctrl === ctrl) {
      activeLoad = null;
      loadingOverlay.hide();
    }
  }
  // Only the load that still owns `activeLoad` (or a fully-finished one) may release the
  // cinematic guard — prevents a superseded load's late unwind from clobbering the new one.
  function releaseLoadBusy(ctrl: AbortController): void {
    if (activeLoad === null || activeLoad.ctrl === ctrl) cinematicBusy = false;
  }
  loadingOverlay.onCancel = () => {
    const al = activeLoad;
    if (!al) return;
    activeLoad = null;
    loadingOverlay.hide();
    al.ctrl.abort();
    // Release the cinematic guard now (the aborted load is still parked on its network
    // wait) so the user can immediately pick a new destination, then run recovery.
    cinematicBusy = false;
    al.cancel();
  };

  let sky: SkyHandle | null = settings.diagSkyEnabled
    ? createSky(scene, cameraCtl.chaseCamera())
    : null;
  let postFx: PostFxHandle | null = settings.diagPostFxEnabled
    ? createPostFx(scene, cameraCtl.chaseCamera(), settings, backend)
    : null;

  const physics = await createPhysicsWorld();
  const input = new Input();
  const chunks = new ChunkManager(scene, physics, DEFAULT_CHUNK_OPTIONS, shadowGenerator);
  const grass = new GrassField(scene, chunks);
  grass.setEnabled(settings.grassEnabled);

  // Both vehicles are created upfront and kept alive for the life of the session. The
  // swap path just toggles which one is enabled (and teleports the activating one to
  // where the deactivating one was). This sidesteps Rapier vehicle-controller / chassis
  // disposal bugs that produced NaN forces / blue-world cascades when we tried to
  // dispose+recreate on every swap. Memory cost is trivial (~one extra rigid body +
  // controller); the disabled vehicle is parked far underground and visually hidden.
  const INACTIVE_PARK = new Vector3(0, -3000, 0);
  const car = new Car(physics, scene, new Vector3(0, 1000, 0));
  const motorcycle = new Motorcycle(physics, scene, INACTIVE_PARK);
  car.setEnabled(false);
  motorcycle.setEnabled(false);

  // The "active" vehicle the player is currently driving. Reassigned by swapVehicle().
  let vehicle: IVehicle = car;

  const wireVehicle = (v: IVehicle): void => {
    v.surfaceLookup = (x, z) => chunks.classifySurface(x, z);
  };
  wireVehicle(car);
  wireVehicle(motorcycle);

  // Visual-overhaul features: shadow casters, landcover-tinted dust, throttle scaling,
  // motion-blur exclusion. These live on Car only — Motorcycle hasn't been wired into
  // VehicleFx / the shadow generator yet, so it renders without dust + casts no shadow
  // (acceptable for now; can be addressed alongside any motorcycle visual pass).
  car.registerShadowCasters(shadowGenerator);
  car.setCoverSampler((x, z) => chunks.sampleCoverColor(x, z));
  car.setPowerMultiplier(settings.carPower);
  if (postFx) {
    for (const m of car.getVisualMeshes()) postFx.excludeFromMotionBlur(m);
  }

  const treeImpacts = new TreeImpactSystem(scene, physics, chunks, vehicle);

  // Current spawn (set by loadAtInner). Held at main scope so vehicle swaps can preserve
  // the "R goes back to where you arrived at this location" behavior — without this the
  // swapped-in vehicle would treat the swap location as its spawn.
  let currentSpawn: { pos: Vector3; yaw: number } | null = null;

  // AI traffic — listens for chunk-ready / chunk-dispose to spawn and despawn cars on the
  // active ring. Wire BEFORE the first chunk loads so spawn-chunk-ready fires the callback.
  const traffic = new TrafficManager(scene, physics, chunks);
  // Snapshot player position for spawn rejection. Returns null while the vehicle is
  // disabled (loading) so traffic spawns aren't blocked at startup.
  traffic.setPlayerPositionProvider(() => (vehicle.isEnabled() ? vehicle.position() : null));
  traffic.setEnabled(true);

  // Audio — created suspended, resumed on first user gesture.
  const audio = new AudioEngine();
  const noiseBuf = createNoiseBuffer(audio.ctx);
  const engineSound = new EngineSound(audio, noiseBuf);
  const impactSound = new ImpactSound(audio, noiseBuf);
  const windSound = new WindSound(audio, noiseBuf);
  const teleportWhoosh = new TeleportWhoosh(audio, noiseBuf);
  const tireSound = new TireSound(audio, noiseBuf);
  const treeImpactSound = new TreeImpactSound(audio, noiseBuf);
  // Whoosh in sync with the teleport zoom-out / zoom-in camera rush — the sound spans the
  // exact camera-move duration so it lands with the motion.
  cinematic.setWhoosh((dir, seconds) => teleportWhoosh.play(dir, seconds));
  // Wire tree felling events → wood SFX. speedKmh from the event is converted
  // back to m/s for the synth's velocity input.
  // Tree-impact SFX with stereo panning + distance attenuation. Position +
  // forward are sampled at the moment of trigger so the land thud (which fires
  // up to ~1 s after the hit) reflects where the tree is relative to the car
  // NOW, not where the car was when it crashed. See `treeSpatial` below.
  treeImpacts.onTreeHit((e) => {
    const s = treeSpatial(vehicle, e.worldX, e.worldZ);
    treeImpactSound.playHit(e.speedKmh / 3.6, e.scale, s.pan, s.attenuation);
  });
  treeImpacts.onTreeLand((e) => {
    const s = treeSpatial(vehicle, e.worldX, e.worldZ);
    treeImpactSound.playLand(e.scale, s.pan, s.attenuation);
  });

  // Suspend all audio while the tab is hidden or the window is unfocused, so the
  // engine isn't audible when the player is on another tab/app. Only auto-resume
  // once the first user gesture has unlocked audio — resuming before that would
  // be rejected by the browser and could start sound the user never enabled.
  let audioUnlocked = false;
  const inFocus = (): boolean => !document.hidden && document.hasFocus();
  const applyAudioActive = (): void => {
    if (!audioUnlocked) return;
    if (inFocus()) void audio.resume();
    else void audio.suspend();
  };

  const resumeAudio = (): void => {
    void audio.resume().then(() => {
      engineSound.start();
      windSound.start();
      tireSound.start();
      audioUnlocked = true;
      applyAudioActive();
    });
    window.removeEventListener('pointerdown', resumeAudio);
    window.removeEventListener('keydown', resumeAudio);
  };
  window.addEventListener('pointerdown', resumeAudio);
  window.addEventListener('keydown', resumeAudio);
  document.addEventListener('visibilitychange', applyAudioActive);
  window.addEventListener('blur', applyAudioActive);
  window.addEventListener('focus', applyAudioActive);

  physics.onContact((h1, h2, started) => {
    if (!started) return;
    const vh = vehicle.colliderHandle();
    if (h1 !== vh && h2 !== vh) return;
    impactSound.play(vehicle.speed());
  });

  // ----- Delivery mode (created before MultiplayerClient so callbacks can reference it) -----
  const orderManager = new ClientOrderManager(scene, chunks);
  const toastsContainer = document.getElementById('toasts');
  if (!toastsContainer) throw new Error('Missing #toasts container');
  const toast = new Toast(toastsContainer);
  const pingSound: PingSound = new PingSound(audio);

  // ----- Multiplayer -----
  const playerName = await resolvePlayerName();
  const mp = new MultiplayerClient(scene, physics, playerName, {
    onWelcome(me) {
      // Apply the assigned player color to BOTH vehicle bodies so the swap shows the
      // right livery on whichever one the player is currently driving.
      car.setChassisColor(me.color);
      motorcycle.setChassisColor(me.color);
      playerList.setSelf(me);
      orderManager.setSelfId(me.id);
    },
    onStatusChange(status) {
      playerList.setStatus(status);
    },
    onTreeFell(ev) {
      treeImpacts.applyRemoteFell(ev.chunkKey, ev.treeIndex, ev.hitDirX, ev.hitDirZ);
    },
    onRejected(reason) {
      console.warn(`[mp] rejected: ${reason}`);
      const status = document.getElementById('statusLine');
      if (status) {
        status.textContent =
          reason === 'full'
            ? 'Server is full (max 4 players)'
            : 'Server rejected connection';
        status.classList.add('error');
      }
    },
    onDeliveryState(state) {
      orderManager.applyFullState(
        state.orders,
        state.mode,
        state.active,
        state.score,
        state.onClock,
        state.isHost,
      );
      hud.setActiveDeliveries(state.active);
      hud.setScore(state.score);
      hud.setSessionMode(state.mode);
      hud.setOnClock(state.onClock);
      hud.setHostControlsVisible(state.isHost);
    },
    onOrderSpawned(order) {
      orderManager.addOrder(order);
      // Sound is driven by the matching delivery_toast 'ping' that the server fans out;
      // playing here would double up.
    },
    onOrderUpdated(order) {
      orderManager.updateOrder(order);
    },
    onOrderRemoved(orderId) {
      orderManager.removeOrder(orderId);
    },
    onActiveDeliveries(active) {
      orderManager.setActive(active);
      hud.setActiveDeliveries(active);
    },
    onScore(score) {
      orderManager.setScore(score);
      hud.setScore(score);
    },
    onMode(mode) {
      orderManager.setMode(mode);
      hud.setSessionMode(mode);
    },
    onHostChanged(hostId) {
      const isMe = mp.myPlayerId() === hostId;
      orderManager.setHost(isMe);
      hud.setHostControlsVisible(isMe);
    },
    onOnClock(on) {
      orderManager.setOnClock(on);
      hud.setOnClock(on);
    },
    onToast(text, flavor) {
      toast.show(text, flavor);
      // Audio cue paired with the toast — server-driven so timing always matches the
      // visible feedback. Pickup is the new addition the user asked for.
      switch (flavor) {
        case 'ping':
          pingSound.play();
          break;
        case 'info':
          pingSound.playPickup();
          break;
        case 'success':
          pingSound.playSuccess();
          break;
        case 'fail':
          pingSound.playFail();
          break;
      }
    },
  });
  treeImpacts.onLocalFell = (ev) => {
    mp.sendTreeFell(ev.chunkKey, ev.idx, ev.dirX, ev.dirZ);
  };
  // Which player a covered teleport-load is currently streaming toward (for the row's
  // "Cancel" affordance), or null.
  let pendingTeleportId: string | null = null;

  // Highest *solid* surface at (tx,tz): cast a ray straight down through the physics world
  // (terrain heightfield + building trimeshes are fixed bodies; the vehicle and traffic are
  // dynamic and excluded) so we land on a building roof rather than inside it. Falls back to
  // the terrain heightmap if nothing is hit.
  function sampleSpawnHeight(tx: number, tz: number): number {
    const terrainH = chunks.sampleHeight(tx, tz) ?? 0;
    const startY = terrainH + 600; // above the tallest building (roofs clamp well under this)
    const ray = new physics.rapier.Ray({ x: tx, y: startY, z: tz }, { x: 0, y: -1, z: 0 });
    const hit = physics.world.castRay(
      ray,
      1200,
      true,
      physics.rapier.QueryFilterFlags.EXCLUDE_DYNAMIC,
    );
    const surfaceY = hit ? startY - hit.timeOfImpact : terrainH;
    return Math.max(surfaceY, terrainH);
  }

  function executeTeleport(tx: number, tz: number, fwdX: number, fwdZ: number): void {
    // Spawn at the chassis's natural rest height so the suspension doesn't have to
    // compress/extend to find equilibrium. With chassisHeight 1.0, wheelAnchorY -0.25,
    // suspensionRestLength 0.45, and the weight of a 1400 kg chassis on stiffness-75
    // springs, the chassis sits roughly 0.95m above the road at rest.
    const ty = sampleSpawnHeight(tx, tz) + 0.95;
    const yaw = Math.atan2(fwdX, fwdZ);
    vehicle.teleportTo(new Vector3(tx, ty, tz), yaw);
    chunks.update(tx, tz);
    cameraCtl.snap(vehicle.position(), vehicle.forward());
  }

  /**
   * Wrap a teleport in the full GTA-style cinematic: camera flies up + clouds fade in,
   * teleport happens behind cover, camera descends from clouds at the new location, then
   * hands back to whichever mode (chase/topdown) is active.
   */
  async function cinematicTeleport(
    teleport: () => void,
    variant: 'full' | 'short',
  ): Promise<void> {
    if (cinematicBusy) return;
    cinematicBusy = true;
    try {
      await cinematic.playOutbound({
        cameraCtl,
        getCarPos: () => vehicle.position(),
        getCarForward: () => vehicle.forward(),
        variant,
      });
      // Thicken to fully opaque before the teleport so the position jump is hidden.
      await cinematic.deepen(0.3);
      teleport();
      // Clouds stay opaque while physics steps for ~0.6s — long enough for the chassis
      // to bottom out, oscillate, and damp to rest before the camera reveals it.
      await cinematic.hold(0.6);
      await cinematic.playInbound({
        cameraCtl,
        getCarPos: () => vehicle.position(),
        getCarForward: () => vehicle.forward(),
        variant,
      });
      // For chase, hand scene.activeCamera back to the real chase camera (cinematic ended
      // at its exact pose). For top-down, scene.activeCamera was never swapped — calling
      // this is a no-op.
      scene.activeCamera = cameraCtl.activeCamera();
    } finally {
      cinematicBusy = false;
    }
  }

  /** Bring the (already-covered) camera back down to the current vehicle — used when a
   *  covered teleport-load is cancelled, so we reveal where the player already is. */
  async function revealCurrent(variant: 'full' | 'short'): Promise<void> {
    await cinematic.playInbound({
      cameraCtl,
      getCarPos: () => vehicle.position(),
      getCarForward: () => vehicle.forward(),
      variant,
    });
    scene.activeCamera = cameraCtl.activeCamera();
  }

  /**
   * Teleport to a target whose chunk isn't loaded yet: fly up + cover, stream the chunk in
   * under the animated clouds (spinner + Cancel visible), then teleport behind cover and
   * descend. Cancelling reveals the player back at their unchanged current location.
   */
  async function teleportToWithLoad(
    tx: number,
    tz: number,
    fwd: { x: number; z: number },
    id: string,
  ): Promise<void> {
    cinematicBusy = true;
    pendingTeleportId = id;
    playerList.setPendingTeleport(id);
    let ctrl: AbortController | null = null;
    try {
      // Fly up to cover (brief, not cancellable — avoids a camera fight with reveal).
      await cinematic.playOutbound({
        cameraCtl,
        getCarPos: () => vehicle.position(),
        getCarForward: () => vehicle.forward(),
        variant: 'full',
      });
      // Now parked under cover on the network wait — show spinner + Cancel.
      ctrl = beginLoad(() => {
        pendingTeleportId = null;
        playerList.setPendingTeleport(null);
        void revealCurrent('full');
      });
      try {
        await chunks.ensureChunkAt(tx, tz);
      } catch {
        if (!ctrl.signal.aborted) {
          endLoad(ctrl);
          pendingTeleportId = null;
          playerList.setPendingTeleport(null);
          await revealCurrent('full');
        }
        return;
      }
      if (ctrl.signal.aborted) {
        // Superseded by a newer load (which aborts ctrl directly, bypassing onCancel):
        // clear our row's pending state so it doesn't stay stuck on "Cancel/loading…".
        if (pendingTeleportId === id) {
          pendingTeleportId = null;
          playerList.setPendingTeleport(null);
        }
        return;
      }

      endLoad(ctrl);
      pendingTeleportId = null;
      playerList.setPendingTeleport(null);
      // Thicken to fully opaque before the jump so the position change is hidden.
      await cinematic.deepen(0.3);
      executeTeleport(tx, tz, fwd.x, fwd.z);
      await cinematic.hold(0.6);
      await cinematic.playInbound({
        cameraCtl,
        getCarPos: () => vehicle.position(),
        getCarForward: () => vehicle.forward(),
        variant: 'full',
      });
      scene.activeCamera = cameraCtl.activeCamera();
    } finally {
      // Release the cinematic guard only if we still own the load (a supersede/cancel may
      // have handed ownership to a newer load, which manages its own guard).
      if (ctrl) releaseLoadBusy(ctrl);
      else cinematicBusy = false;
    }
  }

  const playerList = new PlayerListPanel(
    {
      myPosition: () => vehicle.position(),
      entries: (): PlayerListEntry[] => {
        const out: PlayerListEntry[] = [];
        for (const r of mp.players()) {
          out.push({ identity: r.identity, pos: r.lastKnownPosition() });
        }
        return out;
      },
    },
    (id) => {
      // Clicking the row that's currently loading acts as Cancel (same as the overlay).
      if (pendingTeleportId === id && activeLoad) {
        loadingOverlay.onCancel?.();
        return;
      }
      // Ignore while a world-load (initial spawn / Generate) is tearing down the world.
      if (!vehicle.isEnabled()) return;
      // A different destination supersedes any in-flight load.
      if (activeLoad) activeLoad.ctrl.abort();

      // Find target.
      let targetPos: ReturnType<import('./net/RemotePlayer.js').RemotePlayer['lastKnownPosition']> = null;
      let targetFwd: { x: number; z: number } | null = null;
      for (const r of mp.players()) {
        if (r.identity.id === id) {
          targetPos = r.lastKnownPosition();
          targetFwd = r.lastKnownForward();
          break;
        }
      }
      if (!targetPos) return;
      const pos = targetPos;
      const fwd = targetFwd ?? { x: 0, z: 1 };
      const tx = pos.x - fwd.x * 8;
      const tz = pos.z - fwd.z * 8;

      // Already-loaded chunk → instant cinematic, no loading screen.
      if (chunks.hasChunkAt(tx, tz)) {
        void cinematicTeleport(() => executeTeleport(tx, tz, fwd.x, fwd.z), 'full');
        return;
      }
      // Otherwise cover the screen and stream the chunk in with spinner + Cancel.
      void teleportToWithLoad(tx, tz, fwd, id);
    },
  );
  mp.connect();

  // Settings change handler — shared by the Visual + Debug sections of SettingsModal.
  // (The old SettingsPanel had this inline; lifting it lets both sections trigger the
  // same downstream side-effects.)
  const onSettingsChange = (changed: Set<keyof import('./engine/Settings.js').VisualSettings>): void => {
    // These are baked into materials at scene/chunk creation, so flipping them requires a
    // reload to rebuild every chunk's material. saveSettings is debounced — flush synchronously
    // so the new value is on disk before the page reboots.
    //  - useClassicColors: palette baked per material.
    //  - diagTerrainShadingEnabled: the TerrainShadingPlugin is attached (or not) when the
    //    terrain material is built, so an in-place flag flip leaves existing chunks flat green.
    if (changed.has('useClassicColors') || changed.has('diagTerrainShadingEnabled')) {
      flushSettings(settings);
      window.location.reload();
      return;
    }
    applyDiagnosticFromSettings(settings);
    if ((changed.has('motionBlurEnabled') || changed.has('motionBlurStrength')) && postFx) {
      postFx.setMotionBlur(settings.motionBlurEnabled, settings.motionBlurStrength);
    }
    if (changed.has('animeStyleEnabled') && postFx) {
      postFx.setAnimeStyle(settings.animeStyleEnabled);
    }
    if (changed.has('carPower')) {
      car.setPowerMultiplier(settings.carPower);
    }
    if (changed.has('cameraDistance')) {
      cameraCtl.syncBaselineDistance(settings.cameraDistance);
    }
    if (changed.has('diagShadowsEnabled')) {
      scene.shadowsEnabled = settings.diagShadowsEnabled;
    }
    if (changed.has('fogDistanceMul')) {
      scene.fogStart = PALETTE.fogStart * settings.fogDistanceMul;
      scene.fogEnd = PALETTE.fogEnd * settings.fogDistanceMul;
    }
    if (changed.has('diagPostFxEnabled')) {
      if (!settings.diagPostFxEnabled && postFx) {
        postFx.dispose();
        postFx = null;
      } else if (settings.diagPostFxEnabled && !postFx) {
        postFx = createPostFx(scene, cameraCtl.chaseCamera(), settings, backend);
        for (const m of car.getVisualMeshes()) postFx.excludeFromMotionBlur(m);
      }
    }
    if (changed.has('diagSkyEnabled')) {
      if (!settings.diagSkyEnabled && sky) {
        sky.dispose();
        sky = null;
      } else if (settings.diagSkyEnabled && !sky) {
        sky = createSky(scene, cameraCtl.chaseCamera());
      }
    }
    if (changed.has('grassEnabled')) {
      grass.setEnabled(settings.grassEnabled);
    }
    if (changed.has('showDebugOverlay')) {
      hud.setDebugOverlayVisible(settings.showDebugOverlay);
    }
    if (changed.has('photoMode')) {
      hud.setPhotoMode(settings.photoMode);
    }
  };

  const hud = new Hud({
    onGenerate(name) {
      // A new destination supersedes any in-flight load (loadAtInner aborts the old one).
      void loadByName(name);
    },
    onLoadSaved(loc) {
      hud.setLocationField(loc.shortName);
      void loadAt({ lat: loc.lat, lng: loc.lng, displayName: loc.displayName });
    },
    onVehicleSwap() {
      swapVehicle();
    },
    onToggleClock() {
      const next = !orderManager.getOnClock();
      mp.sendDelivery({ kind: 'delivery_clock', on: next });
    },
    onSetMode(mode) {
      if (!orderManager.isHost()) return;
      mp.sendDelivery({ kind: 'delivery_set_mode', mode });
    },
    onToggleLod(enabled) {
      chunks.setLodEnabled(enabled);
      traffic.setLodEnabled(enabled);
    },
  });
  hud.setLodEnabled(chunks.isLodEnabled());
  hud.setActiveVehicle(vehicle.kind);
  // Sound mute states live in the Sounds section now; HUD has no mute pills to set.
  // Debug overlay starts in whatever state the persisted settings hold.
  hud.setDebugOverlayVisible(settings.showDebugOverlay);

  /**
   * Switch to the other vehicle at the current position + heading. Both vehicles are
   * pre-created and kept alive — we just disable the active one and enable the other
   * after teleporting it onto the active one's spot. Velocity is zeroed so the player
   * doesn't ragdoll out of a 200 km/h spawn. The spawn point (where `R` returns to) is
   * left untouched on the activating vehicle if it was already valid; otherwise we copy
   * `currentSpawn` onto it so R works from the moment of the first swap. No-op while
   * the world is still loading.
   */
  function swapVehicle(): void {
    if (activeLoad || !chunks.isReady()) return;

    const pos = vehicle.position().clone();
    const fwd = vehicle.forward().clone();
    const yaw = Math.atan2(fwd.x, fwd.z);
    const next: IVehicle = vehicle.kind === 'car' ? motorcycle : car;

    // Make sure R goes back to the load-time spawn (not the swap point) on whichever
    // vehicle the player is currently driving.
    if (currentSpawn) next.setSpawn(currentSpawn.pos, currentSpawn.yaw);

    vehicle.setEnabled(false);
    next.teleportTo(pos, yaw);
    next.setEnabled(true);
    vehicle = next;
    treeImpacts.setVehicle(vehicle);

    cameraCtl.snap(vehicle.position(), vehicle.forward());
    hud.setActiveVehicle(vehicle.kind);
    vehiclePref.save(vehicle.kind);
  }
  hud.setLocationField(DEFAULT_LOCATION.displayName.split(',')[0] ?? 'Llanberis');
  // Apply persisted photo-mode state immediately so a reload with the toggle on doesn't
  // briefly flash the full HUD before the user touches anything.
  hud.setPhotoMode(settings.photoMode);

  const minimap = new Minimap(hud.getMinimapCanvas());
  minimap.setOnModeChange((mode) => hud.setMinimapMode(mode));
  hud.setMinimapMode(minimap.getMode());

  // Restore persisted zoom and wire the zoom UI ----------------------------------------
  minimap.setZoomIndex(loadZoomIndex(minimap.getZoomIndex(), ZOOM_LEVELS_M.length - 1));
  const refreshZoomUi = (): void => {
    hud.setMinimapZoomState(minimap.getViewRadiusM(), minimap.canZoomIn(), minimap.canZoomOut());
  };
  minimap.setOnZoomChange(() => {
    saveZoomIndex(minimap.getZoomIndex());
    refreshZoomUi();
  });
  refreshZoomUi();
  hud.getMinimapZoomInButton().addEventListener('click', () => minimap.zoomIn());
  hud.getMinimapZoomOutButton().addEventListener('click', () => minimap.zoomOut());
  // Mouse wheel over the canvas → zoom. preventDefault so the page doesn't scroll.
  hud.getMinimapCanvas().addEventListener(
    'wheel',
    (e) => {
      e.preventDefault();
      if (e.deltaY < 0) minimap.zoomIn();
      else if (e.deltaY > 0) minimap.zoomOut();
    },
    { passive: false },
  );

  // Area-entry notifications --------------------------------------------------------
  const areaIndex = new AreaIndex();
  const placeIndex = new PlaceIndex();
  const areaToast = new AreaToast();
  // Unified Settings modal — opened by the top-left ⚙ button. Four sections in a left
  // rail: Visual, Map, Debug, Sounds. The Visual + Debug sections share `onSettingsChange`
  // (declared above) which re-applies live settings to camera / scene / PostFx / HUD.
  // The Map section's `get()` is read each frame by the area-detection tick; the Sounds
  // section owns its own persistence via audioPrefs.
  const settingsModal = new SettingsModal(hud.getSettingsButton());

  const soundsBindings: SoundsBindings = {
    audio,
    engine: engineSound,
    wind: windSound,
    teleport: teleportWhoosh,
    tire: tireSound,
    impact: impactSound,
    treeImpact: treeImpactSound,
    ping: pingSound,
  };
  const soundsSection = createSoundsSection(soundsBindings);
  /** Last non-zero master volume — used by the M hotkey to restore audio after a
   *  toggle. Initialised from the persisted value so the first M-press after a
   *  reload restores to whatever the user had before. */
  let lastNonZeroMasterVolume = soundsSection.get().master.volume || 0.7;

  const mapSection = createMapSection(() => {
    // Settings are reread directly via mapSection.get() in the per-frame tick, so the
    // callback only needs to exist (used as the change-notification hook).
  });

  settingsModal.addSection(createVisualSection(settings, onSettingsChange));
  settingsModal.addSection(mapSection);
  settingsModal.addSection(createDebugSection(settings, onSettingsChange));
  settingsModal.addSection(soundsSection);
  /** Most-recent region per admin level — when a level's region id changes, fire toast. */
  let lastRegionIds: Partial<Record<AreaTier, number>> = {};
  /** Most-recent place node id (used for nearest-place fallback detection). */
  let lastPlaceId: number | null = null;
  /** Skip the toast on the very first detection — otherwise every session start spams
   *  toasts for every level the player happens to be standing in. */
  let primedRegions = false;
  // Map OSM admin_level → user-facing tier. 7 (Swedish kommun, US township) and 8
  // (most other countries' city/town) both map to "city" — only one will exist for a
  // given country, so the user never sees collisions.
  const LEVEL_TO_TIER: Record<6 | 7 | 8 | 9 | 10, AreaTier> = {
    6: 'county',
    7: 'city',
    8: 'city',
    9: 'suburb',
    10: 'neighbourhood',
  };
  const TIER_TO_LABEL: Record<AreaTier, string> = {
    country: 'Country',
    state: 'Region',
    county: 'County',
    city: 'City',
    suburb: 'District',
    neighbourhood: 'Neighbourhood',
  };
  chunks.setOnChunkPayload((coord, payload) => {
    const bs = payload.osm.boundaries;
    const ps = payload.osm.places;
    if (bs && bs.length > 0) areaIndex.addBoundaries(bs);
    if (ps && ps.length > 0) placeIndex.addPlaces(ps);
    console.info(
      `[area] chunk (${coord.cx},${coord.cz}): +${bs?.length ?? 0} boundaries, +${ps?.length ?? 0} places — totals=${areaIndex.size()}b/${placeIndex.size()}p`,
      ps && ps.length > 0 ? ps.map((p) => `${p.kind}:${p.name}`).slice(0, 8) : '',
    );
  });

  window.addEventListener('resize', () => engine.resize());

  async function loadByName(name: string): Promise<void> {
    hud.setStatus('Geocoding…');
    hud.setBusy(true);
    let location: GeocodeResult;
    try {
      const geo = await geocode(name);
      if (!geo) {
        hud.setStatus(`Could not find "${name}"`, 'error');
        return;
      }
      location = geo;
    } catch (err) {
      console.error(err);
      hud.setStatus('Geocoding failed (is the server running?)', 'error');
      return;
    } finally {
      // Re-enable the location bar before the (covered) chunk load so the user can redirect.
      hud.setBusy(false);
    }
    await loadAtInner(location);
  }

  /** Load from an already-resolved geocode result (saved locations, default spawn). */
  async function loadAt(location: GeocodeResult): Promise<void> {
    await loadAtInner(location);
  }

  async function loadAtInner(location: GeocodeResult): Promise<void> {
    // A new world-load supersedes any in-flight load.
    if (activeLoad) activeLoad.ctrl.abort();

    const frame: SessionFrame = {
      origin: { lat: location.lat, lng: location.lng },
      chunkSize: CHUNK_SIZE,
    };

    // If the player is already in-game (driving), this load is effectively a long-range
    // teleport to a new world: play the full outbound cinematic before tearing down the
    // current chunks. Initial game start has vehicle disabled — skip outbound (the page
    // already opened covered via setCovered at startup) and use the short inbound variant.
    const wasInGame = vehicle.isEnabled();
    const variant: 'full' | 'short' = wasInGame ? 'full' : 'short';
    // What Cancel falls back to: the last place that finished loading (null on first load).
    const prev = previousLocation;

    cinematicBusy = true;
    let ctrl: AbortController | null = null;
    try {
      if (wasInGame) {
        await cinematic.playOutbound({
          cameraCtl,
          getCarPos: () => vehicle.position(),
          getCarForward: () => vehicle.forward(),
          variant,
        });
      }

      // Spinner + Cancel, then prefetch the new origin chunk BEFORE tearing the old world
      // down — so the outgoing area stays rendered (clouds drifting over it) during the
      // network wait instead of an empty blue void. Cancel → reload the previous location,
      // or stay covered (idle) if this is the first-ever load.
      ctrl = beginLoad(() => {
        if (prev) void loadAt(prev);
        else loadingOverlay.idle();
      });

      hud.setStatus('Loading spawn chunk…');
      try {
        await chunks.prefetchOrigin(frame, ctrl.signal);
      } catch (err) {
        if (!ctrl.signal.aborted) {
          console.error(err);
          hud.setStatus('Failed to load spawn chunk', 'error');
          endLoad(ctrl);
        }
        return;
      }
      if (ctrl.signal.aborted) return; // cancelled / superseded during the network wait

      // Thicken the cover to fully opaque over the OLD world before swapping it out, so the
      // old→new terrain change happens behind a solid screen and is never visible. Only
      // meaningful when a world was on screen (in-game); the initial load has nothing behind.
      if (wasInGame) await cinematic.deepen(0.5);
      if (ctrl.signal.aborted) return;

      // New data is in hand — now tear down the old world. The origin chunk builds instantly
      // from the prefetched payload, so the covered "empty" window is just the mesh build.
      vehicle.setEnabled(false);
      chunks.reset(frame);
      // Tree colliders are keyed by chunk coord ("0,0" etc.) which collide across
      // sessions — without an explicit reset, Uppsala's spawn chunk would inherit
      // Stockholm's collider table and its trees wouldn't be felling-collidable.
      treeImpacts.reset();
      minimap.setFrame(frame);
      mp.setFrame(frame);
      orderManager.setFrame(frame);
      // Reset area detection so the first chunk's regions don't fire a toast for the
      // spawn point — primedRegions guard handles the silent first detection.
      areaIndex.clear();
      placeIndex.clear();
      lastRegionIds = {};
      lastPlaceId = null;
      primedRegions = false;

      // Re-cover with clouds at the new origin. setCovered branches by camera mode:
      // chase swaps in the cinematic perspective camera; topdown keeps the topdown ortho
      // active and just dials its zoom up.
      cinematic.setCovered({ cameraCtl, variant });

      try {
        await chunks.ensureChunkAt(0, 0);
      } catch (err) {
        if (!ctrl.signal.aborted) {
          console.error(err);
          hud.setStatus('Failed to load spawn chunk', 'error');
          endLoad(ctrl);
        }
        return;
      }
      if (ctrl.signal.aborted) return; // cancelled / superseded during the load

      // Spawn on the highest solid surface (building roof or terrain), at the chassis's
      // natural rest height (see executeTeleport for derivation).
      const spawn = new Vector3(0, sampleSpawnHeight(0, 0) + 0.95, 0);
      currentSpawn = { pos: spawn.clone(), yaw: 0 };
      vehicle.setSpawn(spawn);
      vehicle.respawn();
      vehicle.setEnabled(true);
      cameraCtl.snap(vehicle.position(), vehicle.forward());
      // Kick off the rest of the active ring so they start streaming immediately.
      chunks.update(0, 0);

      hud.setActiveLocation(location);
      hud.setStatus(`Loaded ${location.displayName}`);
      previousLocation = location;

      // Hide the spinner, then descend into the new area. No lingering hold at altitude for a
      // world-swap — the swap already happened behind the solid cover, so we reveal right away
      // (the cover stays fully opaque for just the build + one frame, then the inbound fades).
      endLoad(ctrl);
      await cinematic.hold(variant === 'full' ? 0 : 0.6);
      await cinematic.playInbound({
        cameraCtl,
        getCarPos: () => vehicle.position(),
        getCarForward: () => vehicle.forward(),
        variant,
      });
      scene.activeCamera = cameraCtl.activeCamera();
    } finally {
      // Release the cinematic guard only if we still own the load.
      if (ctrl) releaseLoadBusy(ctrl);
      else cinematicBusy = false;
    }
  }

  // Initial load = default location. Skip geocoding since DEFAULT_LOCATION already has
  // coords. Once it resolves, restore the player's last-used vehicle (if any) by running
  // the same swap path the V key uses — this guarantees R-spawn is set up correctly on
  // whichever vehicle the player ends up in. Failures (no saved pref, default = car) are
  // a no-op.
  void loadAt(DEFAULT_LOCATION).then(() => {
    const saved = vehiclePref.load();
    if (saved && saved !== vehicle.kind) swapVehicle();
  });

  let last = performance.now();
  engine.runRenderLoop(() => {
    const now = performance.now();
    const dt = Math.min(0.05, (now - last) / 1000);
    last = now;

    const state = input.poll(dt);
    if (state.respawn && chunks.isReady() && !cinematicBusy) {
      void cinematicTeleport(() => {
        vehicle.respawn();
        cameraCtl.snap(vehicle.position(), vehicle.forward());
      }, 'short');
    }
    if (state.toggleMinimapMode) {
      minimap.toggleMode();
    }
    if (state.cycleVehicle) {
      swapVehicle();
    }
    if (state.toggleMute) {
      // M hotkey: toggle master volume between current and 0. Volume IS the mute
      // — there's no separate muted flag in the UI. We stash the pre-mute volume
      // on a side property so unmuting restores it; if it was already at zero
      // when M is pressed (e.g. dragged there) we restore to the default 0.7.
      const prefs = soundsSection.get();
      const current = prefs.master.volume;
      const next = current > 0.001 ? 0 : (lastNonZeroMasterVolume || 0.7);
      if (current > 0.001) lastNonZeroMasterVolume = current;
      prefs.master.volume = next;
      audio.setMasterVolume(next);
      saveAudioPrefs(prefs);
      soundsSection.refresh();
    }
    if (state.toggleCamera && !cinematicBusy) {
      cameraCtl.cycle();
      headlights.setCameraMode(cameraCtl.getMode());
    }
    if (state.toggleTimeOfDay) {
      tod.cycle();
      headlights.setEnabled(tod.get() !== 'day');
    }
    if (state.toggleRoadDebug) {
      hud.toggleRoadDebug();
    }
    if (state.togglePhotoMode) {
      // Flip the persisted setting + the HUD in lockstep. Same path the Settings checkbox
      // takes via onSettingsChange, so the checkbox and the hotkey can't drift apart.
      settings.photoMode = !settings.photoMode;
      saveSettings(settings);
      hud.setPhotoMode(settings.photoMode);
    }

    if (vehicle.isEnabled()) vehicle.beginFrame();
    physics.step(dt, (substepDt) => {
      if (vehicle.isEnabled()) vehicle.applyInput(state, substepDt);
    });

    // Heightfield safety net — Rapier heightfields are one-sided; lift the car back up
    // if it ever ends up under the surface.
    if (vehicle.isEnabled()) {
      const pos = vehicle.position();
      const ground = chunks.sampleHeight(pos.x, pos.z);
      if (ground !== null && pos.y < ground - 0.6) {
        vehicle.recoverTo(pos.x, ground + 1.5, pos.z);
      }
    }

    if (vehicle.isEnabled()) treeImpacts.update(dt);
    // VehicleFx (particle dust / crash impulses) lives on Car only — Motorcycle hasn't
    // been wired into the FX system yet.
    if (vehicle === car && car.isEnabled() && settings.diagVehicleFxEnabled) car.updateFx(dt);

    // Tick AI traffic AFTER physics.step so cars steer/thrust against the freshly-stepped
    // world. Then sync their visuals alongside the player's.
    traffic.update(dt);

    // Tick delivery beams / pin pulse animation.
    orderManager.update(dt);

    vehicle.syncVisual();
    // Pass camera position so traffic cars can drop their wheels past ~60 m. Without this
    // arg, syncVisuals applies no distance LOD (used for the LOD-off A/B comparison the
    // ChunkManager flag drives — but here we always pass it; the per-car method honors the
    // traffic.lodEnabled flag directly). Use the chase camera as the LOD anchor even when
    // the top-down camera is active so wheel pop-in doesn't show up when zoomed out.
    traffic.syncVisuals(cameraCtl.chaseCamera().position);

    // Advance RPM simulation once per frame so all readers see a consistent value.
    vehicle.tickRpm();
    if (vehicle.isEnabled()) {
      engineSound.update(vehicle.getRpmGauge(), vehicle.speed(), vehicle.getEngineInput());
      const kmh = vehicle.speed() * 3.6;
      const sideSource =
        kmh > 80
          ? vehicle.getNearestSideSource(8)
          : { proximity: 0, lateralBias: 0, approachVelocity: 0 };
      windSound.update(
        vehicle.speed(),
        sideSource.proximity,
        sideSource.approachVelocity,
        sideSource.lateralBias,
      );
      tireSound.update(vehicle.speed(), vehicle.getCurrentSurface());
    } else {
      windSound.update(0);
      tireSound.update(0, 'road');
    }

    // Multiplayer: interpolate remote players and emit own snapshot.
    mp.tickRemotePlayers(dt);
    if (vehicle.isEnabled()) mp.maybeSendSnapshot(now, vehicle);

    // Drive streaming. update() handles the active+cached rings; prefetchAhead warms one
    // chunk past the active ring along the travel direction so fast driving (~300 km/h)
    // doesn't outrun the streamer.
    if (vehicle.isEnabled()) {
      const pos = vehicle.position();
      chunks.update(pos.x, pos.z);
      const fwd = vehicle.forward();
      chunks.prefetchAhead(pos.x, pos.z, fwd.x, fwd.z);
    }

    // Crash impulses feed camera shake. Scale factor (~0.15) maps an 8 m/s dv into a
    // visible-but-not-overwhelming shake; tune via settings.cameraShakeIntensity. Only
    // the Car emits these for now (Motorcycle has no impact-impulse pipeline yet).
    if (vehicle === car) cameraCtl.triggerShake(car.consumeImpactImpulse() * 0.15);
    cameraCtl.update(vehicle.position(), vehicle.forward(), vehicle.getForwardSpeed(), dt);

    // Tick billboard-LOD trees so distant quads face the camera. The batch's own update
    // skips work when the chunk isn't at low LOD or the camera barely moved, so this loop
    // is cheap to call unconditionally each frame.
    {
      const camPos = cameraCtl.activeCamera().position;
      for (const chunk of chunks.activeChunks()) {
        chunk.updateBillboardTrees(camPos.x, camPos.z);
      }
    }

    // Near-field grass blades follow the car. update() is a cheap no-op until the car
    // crosses half a grid cell; setTime advances the wind sway every frame.
    if (vehicle.isEnabled()) {
      const gp = vehicle.position();
      grass.setTime(now / 1000);
      grass.update(gp.x, gp.z);
    }

    headlights.update(
      vehicle.position(),
      vehicle.getYaw(),
      cameraCtl.activeCamera().position,
      traffic.getCarsForHeadlights(),
    );

    // Radial speed blur — intensity scaled by current forward speed.
    if (postFx) postFx.updateSpeedBlur(vehicle.getForwardSpeed(), settings.radialBlurIntensity);

    scene.render();

    // Minimap — update after physics so positions are fresh.
    {
      const pos = vehicle.position();
      const fwd = vehicle.forward();
      // Heading: 0 = +Z (north), increases clockwise toward +X (east).
      const headingRad = Math.atan2(fwd.x, fwd.z);
      const states = chunks.isReady() ? chunks.getChunkStates(pos.x, pos.z) : [];
      minimap.update({
        playerX: pos.x,
        playerZ: pos.z,
        headingRad,
        chunks: states,
        trafficCars: traffic.getCarPositions(),
        orderPins: orderManager.getMinimapPins(),
        dt,
      });

      // Area-entry detection — runs only while a session frame is loaded. Compares each
      // admin level's containing polygon id against the last frame; fires a toast on
      // change (subject to the user's per-tier opt-in). primedRegions skips the first
      // detection so the spawn doesn't always toast.
      if (chunks.isReady() && (areaIndex.size() > 0 || placeIndex.size() > 0)) {
        const sessionFrame = chunks.getFrame();
        if (sessionFrame) {
          const { lat, lng } = worldToLatLng(pos.x, pos.z, sessionFrame);
          const notify = mapSection.get();

          // --- Admin-polygon detection (county / suburb-district from level 9) ---
          const regions = areaIndex.regionsAt(lat, lng);
          const nextIds: Partial<Record<AreaTier, number>> = {};
          for (const [level, region] of regions.entries()) {
            const tier = LEVEL_TO_TIER[level];
            nextIds[tier] = region.id;
            if (
              primedRegions &&
              notify[tier] &&
              lastRegionIds[tier] !== region.id
            ) {
              console.info(
                `[area] tier=${tier} polygon change: ${lastRegionIds[tier] ?? 'none'} -> ${region.id} (${region.name})`,
              );
              areaToast.show(region.name, TIER_TO_LABEL[tier]);
            }
          }
          lastRegionIds = nextIds;

          // --- Place-node nearest-neighbour detection (Flogsta, Stenhagen, …) ---
          // Restrict to sub-city scales — city/town nodes are already covered by admin
          // polygons, and we don't want a "neighbourhood: Uppsala" toast firing.
          const nearestPlace = placeIndex.nearestWithHysteresis(lat, lng, (k) =>
            k === 'suburb' || k === 'neighbourhood' || k === 'quarter' || k === 'hamlet' || k === 'locality',
          );
          if (nearestPlace) {
            // All sub-city place kinds map to the "neighbourhood" tier in the settings UI.
            if (
              primedRegions &&
              notify.neighbourhood &&
              lastPlaceId !== nearestPlace.id
            ) {
              console.info(
                `[area] place change: ${lastPlaceId ?? 'none'} -> ${nearestPlace.id} (${nearestPlace.kind}:${nearestPlace.name})`,
              );
              areaToast.show(nearestPlace.name, TIER_TO_LABEL.neighbourhood);
            }
            lastPlaceId = nearestPlace.id;
          } else {
            lastPlaceId = null;
          }

          // --- Build the persistent "current area" label on the minimap card ---
          // Max 2 parts: one neighbourhood-scope name + one city-scope name.
          //   - Neighbourhood: prefer level-10 polygon → else level-9 polygon → else
          //     nearest place node from PlaceIndex.
          //   - City: prefer level-7 polygon (Swedish kommun) → else level-8 polygon.
          //   - Level-6 (county) is skipped entirely — too coarse for the label.
          //   - Strip the verbose English " Municipality" suffix on the city-scope name
          //     so e.g. "Stockholm Municipality" → "Stockholm". Native suffixes like
          //     " kommun" are left alone.
          //   - De-dupe by exact name so a level-10 polygon named "Uppsala" doesn't
          //     double the city name.
          const neighbourhoodName =
            regions.get(10)?.name ?? regions.get(9)?.name ?? nearestPlace?.name ?? null;
          const cityRaw = regions.get(7)?.name ?? regions.get(8)?.name ?? null;
          const cityName = cityRaw ? cityRaw.replace(/\s+Municipality$/, '') : null;
          const labelParts: string[] = [];
          if (neighbourhoodName) labelParts.push(neighbourhoodName);
          if (cityName && cityName !== neighbourhoodName) labelParts.push(cityName);
          hud.setMinimapArea(labelParts);

          // Periodic snapshot so we can see what's happening even when no change occurs.
          if (!primedRegions || Math.random() < 0.005) {
            console.info(
              `[area] tick lat=${lat.toFixed(5)} lng=${lng.toFixed(5)} regions=`,
              Array.from(regions.entries()).map(([lv, r]) => `${lv}:${r.name}`),
              `nearestPlace=${nearestPlace ? `${nearestPlace.kind}:${nearestPlace.name}` : 'none'}`,
              `idx=${areaIndex.size()}b/${placeIndex.size()}p`,
            );
          }
          primedRegions = true;
        }
      } else {
        // No region data loaded yet (or session not ready) — clear the label so it
        // doesn't show stale info from a previous session.
        hud.setMinimapArea([]);
      }
    }

    const fps = engine.getFps();
    hud.setFps(fps, now);
    // GPU frame time is in nanoseconds, and the counter is null when timer-query isn't
    // supported (e.g. WebGL without EXT_disjoint_timer_query). Triangles is the most
    // useful proxy for LOD impact since indices count drops with every degraded mesh.
    const gpuCounter = engineInstrumentation.gpuFrameTimeCounter;
    const gpuNs = gpuCounter ? gpuCounter.current : 0;
    const gpuMs = gpuNs > 0 ? gpuNs / 1_000_000 : null;
    hud.setPerfStats(
      {
        gpuMs,
        drawCalls: sceneInstrumentation.drawCallsCounter.current,
        activeTris: Math.round(scene.getActiveIndices() / 3),
      },
      now,
    );
    hud.setRpm(vehicle.getRpmGauge());
    hud.setSpeed(vehicle.speed() * 3.6);
    hud.tickDeliveryTimers(mp.serverNow());

    // Road-class debug readout. Lookup is gated on visibility so toggled-off has zero cost.
    if (hud.isRoadDebugVisible() && vehicle.isEnabled()) {
      const pos = vehicle.position();
      hud.setRoadDebug(chunks.classifyRoadDetail(pos.x, pos.z));
    }

    playerList.update(now);
  });
}

/**
 * Compute stereo pan + distance attenuation for a sound source at world (tx, tz)
 * relative to the current car position and heading. Used by the tree-impact SFX
 * subscribers so a tree that was hit on the right pans right, and the land thud
 * fades naturally as the car drives away from it.
 *
 *   pan         -1 = full left, +1 = full right, 0 = directly ahead/behind.
 *               (Cheap stereo can't distinguish ahead from behind — that's a
 *               known limitation; would need HRTF or a fake-reverb cue.)
 *   attenuation 0..1, piecewise-linear: full within 5 m, ramps down to silence
 *               at 60 m. 0 lets the audio class skip the node graph entirely.
 */
function treeSpatial(
  vehicle: IVehicle,
  tx: number,
  tz: number,
): { pan: number; attenuation: number } {
  const pos = vehicle.position();
  const fwd = vehicle.forward();
  const dx = tx - pos.x;
  const dz = tz - pos.z;
  const dist = Math.hypot(dx, dz);
  // World right = up × forward = (forward.z, 0, -forward.x) in left-handed Babylon.
  // pan = (treeOffset · carRight) / |treeOffset|
  const pan = dist < 1e-3 ? 0 : clampPan((dx * fwd.z - dz * fwd.x) / dist);
  const attenuation = distanceAttenuation(dist);
  return { pan, attenuation };
}

function distanceAttenuation(dist: number): number {
  if (dist <= 5) return 1;
  if (dist >= 60) return 0;
  return 1 - (dist - 5) / 55;
}

function clampPan(v: number): number {
  return v < -1 ? -1 : v > 1 ? 1 : v;
}

main().catch((err) => {
  console.error('Fatal init error', err);
  const status = document.getElementById('statusLine');
  if (status) {
    status.textContent = 'Fatal init error — see console';
    status.classList.add('error');
  }
});
